import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtIDR, fmtDateShort, wibDateISO, wibDaysAgo } from "../lib/api";
import { PageHeader, Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { RefreshCw, Wallet, Clock, ShoppingBag, Receipt, Settings as SettingsIcon, Search, ChevronLeft, ChevronRight, Zap } from "lucide-react";

const daysAgo = (n) => wibDaysAgo(n);
const today = () => wibDateISO();

const Card = ({ icon: Icon, label, value, sub, tone = "text-cocoa" }) => (
  <Panel className="p-4">
    <div className="flex items-center gap-2 text-wmuted text-xs uppercase tracking-wide"><Icon className="w-4 h-4" /> {label}</div>
    <div className={`text-2xl font-bold mt-1 ${tone}`}>{value}</div>
    {sub && <div className="text-xs text-wmuted mt-0.5">{sub}</div>}
  </Panel>
);

export default function OnlineSales() {
  const [start, setStart] = useState(daysAgo(30));
  const [end, setEnd] = useState(today());
  const [marketplace, setMarketplace] = useState("all");
  const [settled, setSettled] = useState("all");
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [data, setData] = useState({ configured: true, items: [], summary: {}, total: 0, page: 1, pages: 1, limit: 25 });
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/online-sales", { params: { start, end, marketplace, settled, q: q || undefined, page, limit } })
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, limit, q, marketplace, settled, start, end]);
  const applySearch = () => { setQ(qInput); setPage(1); };

  const s = data.summary || {};

  return (
    <div data-testid="online-sales-page">
      <PageHeader title="Ventedaily Online Sales" subtitle="Rekap transaksi Shopee & TikTok beserta status pencairan settlement">
        <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-online-sales"><RefreshCw className="w-4 h-4 mr-1.5" /> Muat Ulang</Button>
      </PageHeader>

      {data.configured === false ? (
        <Panel className="p-10 text-center text-wmuted">
          <SettingsIcon className="w-10 h-10 mx-auto mb-3 opacity-40" />
          Belum ada customer yang ditetapkan untuk Ventedaily Online Sales.
          <div className="mt-2 text-sm">Atur dulu di <Link to="/settings" className="text-cocoa underline font-medium">Settings → Ventedaily Online Sales</Link>.</div>
        </Panel>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Card icon={ShoppingBag} label="Total Transaksi" value={s.count || 0} sub={`Penjualan ${fmtIDR(s.gross)}`} />
            <Card icon={Receipt} label="Total Penjualan" value={fmtIDR(s.gross)} />
            <Card icon={Wallet} label="Sudah Cair" value={fmtIDR(s.cair_amount)} sub={`${s.cair_count || 0} transaksi (nett settlement)`} tone="text-status-passed" />
            <Card icon={Clock} label="Belum Cair" value={fmtIDR(s.belum_amount)} sub={`${s.belum_count || 0} transaksi`} tone="text-status-review" />
          </div>

          <Panel className="p-4 mb-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="relative flex-1 min-w-[200px] max-w-xs">
                <Label className="text-xs">Cari</Label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
                  <Input value={qInput} onChange={(e) => setQInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applySearch()} placeholder="No. sales / order id / customer..." className="pl-9 bg-white" data-testid="os-search" />
                </div>
              </div>
              <div><Label className="text-xs">Dari</Label><Input type="date" value={start} onChange={(e) => { setStart(e.target.value); setPage(1); }} className="w-40" data-testid="filter-start" /></div>
              <div><Label className="text-xs">Sampai</Label><Input type="date" value={end} onChange={(e) => { setEnd(e.target.value); setPage(1); }} className="w-40" data-testid="filter-end" /></div>
              <div>
                <Label className="text-xs">Marketplace</Label>
                <Select value={marketplace} onValueChange={(v) => { setMarketplace(v); setPage(1); }}>
                  <SelectTrigger className="w-36" data-testid="filter-marketplace"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua</SelectItem>
                    <SelectItem value="Shopee">Shopee</SelectItem>
                    <SelectItem value="TikTok">TikTok</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Pencairan</Label>
                <Select value={settled} onValueChange={(v) => { setSettled(v); setPage(1); }}>
                  <SelectTrigger className="w-36" data-testid="filter-settled"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua</SelectItem>
                    <SelectItem value="cair">Sudah Cair</SelectItem>
                    <SelectItem value="belum">Belum Cair</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={applySearch} disabled={loading} data-testid="apply-filter" className="bg-cocoa hover:bg-cocoa-dark text-ivory">Terapkan</Button>
            </div>
          </Panel>

          <Panel>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>No. Sales</TableHead>
                  <TableHead>Order ID</TableHead>
                  <TableHead>Marketplace</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Tanggal</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-center">Pencairan</TableHead>
                  <TableHead className="text-right">Nett Cair</TableHead>
                  <TableHead>Tgl Cair</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((it) => (
                  <TableRow key={it.id} data-testid={`os-row-${it.id}`}>
                  <TableCell className="font-medium text-cocoa">
                    {it.number}
                    {it.is_kilat && (
                      <span title="Pesanan Kilat Shopee (FBS)" data-testid={`os-kilat-badge-${it.id}`}
                        className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 px-1.5 py-0.5 text-[10px] font-semibold align-middle">
                        <Zap className="w-3 h-3" /> Kilat
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {it.is_kilat && it.reservation_id && it.reservation_id !== it.order_id ? (
                      <div className="space-y-0.5">
                        <div><span className="text-wmuted">Pesanan:</span> {it.order_id || "-"}</div>
                        <div><span className="text-wmuted">Reservasi:</span> {it.reservation_id}</div>
                      </div>
                    ) : it.is_kilat ? (
                      <div><span className="text-wmuted">Reservasi:</span> {it.reservation_id || it.order_id || "-"}</div>
                    ) : (it.order_id || "-")}
                  </TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{it.marketplace}</Badge></TableCell>
                    <TableCell className="text-sm">{it.customer_name}</TableCell>
                    <TableCell className="text-sm">{fmtDateShort(it.created_at)}</TableCell>
                    <TableCell className="text-right">{fmtIDR(it.grand_total)}</TableCell>
                    <TableCell className="text-center">
                      {it.settled
                        ? <Badge className="bg-status-passed/15 text-status-passed border-0" data-testid={`badge-cair-${it.id}`}>Cair</Badge>
                        : <Badge variant="outline" className="border-status-review text-status-review" data-testid={`badge-belum-${it.id}`}>Belum</Badge>}
                    </TableCell>
                    <TableCell className="text-right text-sm">{it.settled ? fmtIDR(it.nett_cair) : "-"}</TableCell>
                    <TableCell className="text-sm">{it.settled ? fmtDateShort(it.released_date) : "-"}</TableCell>
                  </TableRow>
                ))}
                {data.items.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center text-wmuted py-8">Tidak ada transaksi pada rentang ini.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between px-4 py-3 border-t border-taupe/15 text-sm">
              <div className="flex items-center gap-3">
                <span className="text-wmuted">Tampilkan</span>
                <Select value={String(limit)} onValueChange={(v) => { setLimit(parseInt(v)); setPage(1); }}>
                  <SelectTrigger className="w-20 h-8 bg-white" data-testid="os-page-size"><SelectValue /></SelectTrigger>
                  <SelectContent>{[10, 20, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                </Select>
                <span className="text-wmuted">
                  {data.total ? `${(data.page - 1) * data.limit + 1}–${Math.min(data.page * data.limit, data.total)} dari ${(data.total || 0).toLocaleString("id-ID")}` : "0 data"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-wmuted">Halaman {data.page || 1}/{data.pages || 1}</span>
                <Button size="sm" variant="outline" disabled={(data.page || 1) <= 1} onClick={() => setPage(page - 1)} className="border-taupe text-cocoa" data-testid="os-prev"><ChevronLeft className="w-4 h-4" /></Button>
                <Button size="sm" variant="outline" disabled={(data.page || 1) >= (data.pages || 1)} onClick={() => setPage(page + 1)} className="border-taupe text-cocoa" data-testid="os-next"><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
